// src/features/rooms/PlaceDevicesFromStandardRoomsModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { FaBolt, FaInfoCircle } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectStandardRoomPlacementStatsInSelectedRooms } from "../../../Room/roomSelectors";
import { thunkPlaceDevicesFromStandardRooms } from "./thunkPlaceDevicesFromStandardRooms";

type Props = {
  opened: boolean;
  onClose: () => void;
};

type ResultLite = {
  targetRoomsCapped: number;
  uniqueStandardRooms: number;
  totalItemsFound: number;
  undertakDevicesFound: number;
  copiedDevicesCount: number;
  failedTargetRoomsCount: number;
  failedTargetRooms: string[];
};

type InfoBlock = { title: string; lines: string[]; color: "gray" | "green" | "red" };

export const PlaceDevicesFromStandardRoomsModal: React.FC<Props> = ({ opened, onClose }) => {
  const dispatch = useAppDispatch();

  // stats brukes for oppstartstekst + cap-warning
  const stats = useAppSelector(selectStandardRoomPlacementStatsInSelectedRooms);

  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);

  const [statusText, setStatusText] = useState<string>("");
  const [result, setResult] = useState<ResultLite | null>(null);
  const [hadError, setHadError] = useState(false);

  // guard for React 18 strict mode double effects
  const startedForOpenRef = useRef(false);

  const isCapped = useMemo(() => {
    const placeable = stats.placeableRoomNumbers?.length ?? 0;
    return placeable > 100;
  }, [stats.placeableRoomNumbers?.length]);

  const summaryText = useMemo(() => {
    const total = stats.selectedCount;
    const without = stats.roomsWithoutDevicesCount;
    const placeable = stats.placeableCount;

    return `Av ${total} valgte rom finnes ${without} rom som ikke har noen komponenter. Av disse ${without} er ${placeable} rom som har et standardrom.`;
  }, [stats.selectedCount, stats.roomsWithoutDevicesCount, stats.placeableCount]);

  const canPlace = useMemo(() => {
    if (isPreviewLoading || isPlacing) return false;
    if (!result) return false;
    return result.copiedDevicesCount > 0;
  }, [isPreviewLoading, isPlacing, result]);

  const failedRoomsText = useMemo(() => {
    if (!result) return null;
    if (result.failedTargetRoomsCount === 0) return null;

    const first = result.failedTargetRooms.slice(0, 8).join(", ");
    const rest =
      result.failedTargetRoomsCount > 8 ? ` +${result.failedTargetRoomsCount - 8} flere` : "";
    return `Kunne ikke kopiere til ${result.failedTargetRoomsCount} rom (for store avvik): ${first}${rest}`;
  }, [result]);

  const infoBlock = useMemo<InfoBlock | null>(() => {
    if (!opened) return null;

    // 1) Pågår (preview eller placing)
    if (isPreviewLoading || isPlacing) {
      return {
        title: "Pågår",
        lines: [statusText || "Jobber..."],
        color: "gray",
      };
    }

    // 2) Feil
    if (hadError) {
      return {
        title: "Feil",
        lines: ["Kunne ikke hente/analysere standardrom. Se toast for detaljer."],
        color: "red",
      };
    }

    // 3) Ingen result ennå (t.ex. 0 rom)
    if (!result) {
      return {
        title: "Resultat",
        lines: ["Ingen data tilgjengelig."],
        color: "gray",
      };
    }

    // 4) Resultat (ferdig preview eller etter placing)
    const targetRooms = result.targetRoomsCapped;
    const found = result.totalItemsFound;
    const undertakInStandardRooms = result.undertakDevicesFound;
    const willPlace = result.copiedDevicesCount;

    return {
      title: "Resultat",
      lines: [
        `Målrom: ${targetRooms} rom.`,
        `Kilde (standardrom): Fant ${found} komponenter totalt, hvorav ${undertakInStandardRooms} er identifisert som himlingskomponenter.`,
        `Utplassering (målrom): ${willPlace} himlingskomponenter vil bli plassert etter kopiering.`,
      ],
      color: willPlace > 0 ? "green" : "gray",
    };
  }, [opened, isPreviewLoading, isPlacing, statusText, hadError, result]);

  // Preview når modalen åpnes
  useEffect(() => {
    if (!opened) {
      startedForOpenRef.current = false;
      setResult(null);
      setStatusText("");
      setIsPreviewLoading(false);
      setIsPlacing(false);
      setHadError(false);
      return;
    }
    if (startedForOpenRef.current) return;
    startedForOpenRef.current = true;

    const runPreview = async () => {
      setHadError(false);
      setIsPreviewLoading(true);
      setStatusText("Analyserer valgte rom og henter komponenter fra standardrom...");
      setResult(null);

      try {
        const res = await dispatch(
          thunkPlaceDevicesFromStandardRooms({ cap: 100, addToNonPublished: false })
        ).unwrap();

        setResult({
          targetRoomsCapped: res.targetRoomsCapped,
          uniqueStandardRooms: res.uniqueStandardRooms,
          totalItemsFound: res.totalItemsFound,
          undertakDevicesFound: res.undertakDevicesFound,
          copiedDevicesCount: res.copiedDevicesCount,
          failedTargetRoomsCount: res.failedTargetRoomsCount,
          failedTargetRooms: res.failedTargetRooms,
        });

        setStatusText("Ferdig! Se oppsummeringen under.");
      } catch (e) {
        console.error(e);
        setHadError(true);
      } finally {
        setIsPreviewLoading(false);
      }
    };

    void runPreview();
  }, [opened, dispatch]);

  const handlePlace = async () => {
    if (isPreviewLoading || isPlacing) return;

    setHadError(false);
    setIsPlacing(true);
    setStatusText("Plasserer komponenter i valgte rom...");

    try {
      const res = await dispatch(
        thunkPlaceDevicesFromStandardRooms({ cap: 100, addToNonPublished: true })
      ).unwrap();

      setResult({
        targetRoomsCapped: res.targetRoomsCapped,
        uniqueStandardRooms: res.uniqueStandardRooms,
        totalItemsFound: res.totalItemsFound,
        undertakDevicesFound: res.undertakDevicesFound,
        copiedDevicesCount: res.copiedDevicesCount,
        failedTargetRoomsCount: res.failedTargetRoomsCount,
        failedTargetRooms: res.failedTargetRooms,
      });

      // Lukk ved suksess hvis noe faktisk ble lagt til
      if (res.copiedDevicesCount > 0) {
        onClose();
      }
    } catch (e) {
      console.error(e);
      setHadError(true);
    } finally {
      setIsPlacing(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={isPreviewLoading || isPlacing ? () => {} : onClose}
      title={<Text fw={700}>Plasser ut komponenter fra standardrom</Text>}
      centered
      size="lg"
    >
      <Stack gap="sm">
        <Alert icon={<FaInfoCircle />} title="Oppsummering" color="blue">
          {summaryText}
        </Alert>

        {isCapped && (
          <Alert color="yellow" title="Begrensning">
            Du har valgt mer enn 100 rom som kan motta komponenter. Vi behandler kun de første{" "}
            <b>100</b> rommene i denne operasjonen.
          </Alert>
        )}

        <Group gap="xs">
          <Badge variant="light">
            Status: {isPlacing ? "Plasserer..." : isPreviewLoading ? "Henter..." : "Klar"}
          </Badge>

          {result && (
            <>
              <Badge variant="light">Unike standardrom: {result.uniqueStandardRooms}</Badge>
              <Badge variant="light">Utplassering: {result.copiedDevicesCount}</Badge>
            </>
          )}
        </Group>

        {/* ✅ Kun én boks for status/resultat */}
        {infoBlock && (
          <Alert icon={<FaBolt />} title={infoBlock.title} color={infoBlock.color}>
            <Stack gap={4}>
              {infoBlock.lines.map((l) => (
                <Text key={l} size="sm">
                  {l}
                </Text>
              ))}

              {failedRoomsText && infoBlock.title === "Resultat" && (
                <Text size="sm" c="dimmed" mt={6}>
                  {failedRoomsText}
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose} disabled={isPreviewLoading || isPlacing}>
            Lukk
          </Button>

          <Button onClick={handlePlace} disabled={!canPlace} loading={isPlacing}>
            Sett ut komponenter
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
